import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import isString from 'lodash.isstring';
import {GroupSuggest} from 'src/components/group-suggest';
import {DateRangeInput} from 'src/components/input';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {RequiredMark} from 'src/components/required-mark';
import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidatedControl} from 'src/contexts/validation-context';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {
    selectAccountLicense,
} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {analyzeUsageCriteria} from 'src/openapi/admin/apis/UsageReportsCriteriaApi';
import {getUsageReportSync} from 'src/openapi/admin/apis/UsageReportsV2Api';
import {ReportType} from 'src/openapi/admin/models/ReportType';
import {UserStateCriteria, UserStateCriteria as UserStates} from 'src/openapi/admin/models/UserStateCriteria';

import Button from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import DropDownTree from '@pb/kit-component-dropdown-tree';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';
import Typography from '@pb/kit-component-typography';

import {BigReportWarningModal} from '../../components/big-report-warning-modal';
import {UserStateSuggest} from '../../components/user-state-suggest';
import {UsersSuggest} from '../../components/users-suggest';
import {isValidDateRange, validateEndDate} from '../../components/utils/dateValidationUtils';
import {REPORTS_SUB_TABS} from '../../constants';

import {
    DATE_RANGE_NAME,
    DATE_RANGE_OPTIONS,
    PROGRESS_LABEL,
    REPORT_OPTIONS_TREE,
    REPORT_TASK,
    SELECT_NAME,
    SELECT_OPTIONS,
} from './constants';
import {ExcelPluginUsageReportDrawer} from './excel-plugin-usage-report-drawer';
import {GeneratedExcelPluginUsageReports} from './generated-reports/generated-excel-plugin-usage-reports';
import {excelPluginUsageReportsActions} from './generated-reports/slices/excel-plugin-usage-reports.slice';

import type {GeneratedReport} from './typedef';
import type {UserSearchResult} from 'src/openapi/admin/models/UserSearchResult';
import type {UserUsageCriteria} from 'src/openapi/admin/models/UserUsageCriteria';
import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';


type Props = {
    accountId: number,
};

export const ExcelPluginUsageReportSettings = ({accountId}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useDispatch<ApiDispatchForToolkit>();

    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {getCalendarMonthPeriod} = useDateTime();
    const createBackgroundTask = useBackgroundTaskManager();

    const [selectedReport, selectReport] = useState<GeneratedReport | undefined>(undefined);

    const license = useSelector(selectAccountLicense);

    const [criteria, setCriteria] = React.useState<UserUsageCriteria | null>(null);

    const [dateRange, setDateRange] = useState(DATE_RANGE_OPTIONS.CUSTOM.value);
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [userStates, selectUserStates] = useState<UserStateCriteria[]>([UserStates.LICENSED]);

    const [selectMode, setSelectMode] = useState(SELECT_OPTIONS.GROUPS.value);
    const [selectedUsers, setSelectedUsers] = useState<(UserSearchResult | string)[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
    const [reportOptions, setReportOptions] = useState<string[]>([
        'companySummaryReport',
        'userSummaryReport',
        'userActivityTrend',
        'projectCodeSummaryReport',
    ]);
    const [projectCode, setProjectCode] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (license) {
            setStartDate(license.startDate);
            setEndDate(license.endDate);
        }
    }, [license]);

    const onBigReportRequested = useCallback(() => {
        setSuccessMessage('The report will be available for download in Generated Reports section');
        setCriteria(null);
    }, [setSuccessMessage]);

    const closeBigReportNotification = useCallback(() => {
        setCriteria(null);
    }, []);

    const openBigReportNotification = useCallback((request: UserUsageCriteria) => {
        setCriteria(request);
    }, []);

    const closeDrawer = useCallback(() => {
        selectReport(undefined);
    }, []);

    const downloadReportSync = useCallback(async (userUsageCriteria: UserUsageCriteria) => {
        const backgroundTask = createBackgroundTask(
            REPORT_TASK,
            <Label>{PROGRESS_LABEL}</Label>,
            {
                maxStack: 1,
                stackOverflowMessage: STACKOVERFLOW_MESSAGE,
                cancelable: true,
                cancelMessage: 'The downloading is canceled',
            },
        );

        if (backgroundTask) {
            const response = await apiDispatch(getUsageReportSync({
                userUsageCriteria,
                reportType: ReportType.EXCEL_REPORT,
            }));

            if (response.payload.error) {
                backgroundTask.completeFailure(
                    <b>{response.payload.data.message || 'Failed to download report. Please, try again later.'}</b>,
                );
            } else {
                backgroundTask.completeSuccess(<b>The file has been downloaded successfully</b>);
                dispatch(excelPluginUsageReportsActions.requireRefresh());
            }
        }
    }, [createBackgroundTask, apiDispatch, dispatch]);

    const analyzeCriteria = useCallback(async () => {
        if (!startDate || !endDate) {
            return;
        }

        const groupId = selectMode === SELECT_OPTIONS.GROUPS.value
            ? selectedGroup || undefined
            : undefined;
        const userIds = selectMode === SELECT_OPTIONS.USERS.value && selectedUsers.length
            ? selectedUsers
                    .filter(value => !isString(value))
                    .map(value => (value as UserSearchResult).id)
            : undefined;

        const userUsageCriteria: UserUsageCriteria = {
            accountId,
            startDate,
            endDate,
            userStates,
            projectCode,
            groupId,
            userIds,
            ...Object.fromEntries(reportOptions.map(option => [option, true])),
        };

        const response = await apiDispatch(analyzeUsageCriteria({userUsageCriteria}));
        if (response.payload.success) {
            const isBigReport = response.payload.data.bigReport;
            if (isBigReport) {
                openBigReportNotification(userUsageCriteria);
            } else {
                downloadReportSync(userUsageCriteria);
            }
        } else {
            setErrorMessage(response.payload.data.message || 'Failed to analyze criteria. Please, try again later.');
        }
    }, [
        accountId,
        apiDispatch,
        userStates,
        openBigReportNotification,
        downloadReportSync,
        setErrorMessage,

        selectMode,
        selectedGroup,
        selectedUsers,
        reportOptions,
        startDate,
        endDate,
        projectCode,
    ]);

    const changeMode = useCallback((mode: string) => {
        closeDrawer();
        if (mode === DATE_RANGE_OPTIONS.CURRENT_LICENSE.value && license) {
            setStartDate(license.startDate);
            setEndDate(license.endDate);
        } else if (mode === DATE_RANGE_OPTIONS.LAST_MONTH.value) {
            const period = getCalendarMonthPeriod();
            setStartDate(period.startDate);
            setEndDate(period.endDate);
        }

        setDateRange(mode);
    }, [license, getCalendarMonthPeriod, closeDrawer]);

    const changeStartDate = useCallback((date: string) => {
        closeDrawer();

        if (date !== startDate) {
            setDateRange(DATE_RANGE_OPTIONS.CUSTOM.value);
        }

        setStartDate(date);
    }, [startDate, closeDrawer]);

    const changeEndDate = useCallback((date: string) => {
        closeDrawer();

        if (date !== endDate) {
            setDateRange(DATE_RANGE_OPTIONS.CUSTOM.value);
        }

        setEndDate(date);
    }, [endDate, closeDrawer]);

    const validateEndDateWrapper = useCallback(() => {
        return validateEndDate(endDate, startDate, 'USAGE_REPORT_END_DATE');
    }, [endDate, startDate]);


    const changeSelectionMode = useCallback((mode: string) => {
        setSelectMode(mode);
        closeDrawer();
        if (mode === SELECT_OPTIONS.GROUPS.value) {
            setSelectedUsers([]);
            setProjectCode(undefined);
            selectUserStates([UserStates.LICENSED]);
        } else {
            setSelectedGroup(null);
        }
    }, [closeDrawer]);

    const changeSelectedUsers = useCallback((users: (UserSearchResult | string)[]) => {
        setSelectedUsers(users);
        setProjectCode(undefined);

        if (users.length) {
            selectUserStates([]);
        } else {
            selectUserStates([UserStates.LICENSED]);
        }
    }, []);

    const isRunDisabled = useMemo(() => {
        if (!reportOptions.length) {
            return true;
        }

        const validUsers = selectedUsers.filter(value => !isString(value));

        if (selectedUsers.length) {
            return !validUsers.length;
        }

        return !(userStates.length && isValidDateRange(endDate, startDate));
    }, [reportOptions, selectedUsers, userStates, startDate, endDate]);

    return (
        <ProfileMain
            header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            {REPORTS_SUB_TABS.EXCEL_PLUGIN_USAGE_REPORT.caption}
                        </Typography>
                        <Button disabled={isRunDisabled} variant="Type.PRIMARY" onClick={analyzeCriteria}>
                            Run Report
                        </Button>
                    </Group>
                </Item>
            )}
            footer={<GeneratedExcelPluginUsageReports accountId={accountId} onOpenReport={selectReport}/>}>
            <FormSection headline="Configuration">
                <Group direction="v">
                    <Group direction="v" gap="xs">
                        <Label><RequiredMark label="Date Range"/></Label>
                        <RadioButtons
                            name={DATE_RANGE_NAME}
                            value={dateRange}
                            onChange={changeMode}>
                            <Group gap="s">
                                <RadioButton {...DATE_RANGE_OPTIONS.CUSTOM}/>
                                <RadioButton {...DATE_RANGE_OPTIONS.LAST_MONTH}/>
                                <RadioButton {...DATE_RANGE_OPTIONS.CURRENT_LICENSE}/>
                            </Group>
                        </RadioButtons>
                        <DateRangeInput
                            id="usage-report-start-date-picker"
                            label={<>Starts</>}
                            width={300}
                            clearable={true}
                            startDate={startDate}
                            endDate={endDate}
                            selectRangeType="start"
                            onChange={changeStartDate}
                            onFocus={closeDrawer}
                        />
                        <ValidatedControl
                            name="USAGE_REPORT_END_DATE"
                            validate={validateEndDateWrapper}>
                            {(invalid: boolean) => (
                                <DateRangeInput
                                    id="usage-report-end-date-picker"
                                    label={<>Ends&nbsp;&nbsp;</>}
                                    width={300}
                                    clearable={true}
                                    invalid={invalid}
                                    startDate={startDate}
                                    endDate={endDate}
                                    selectRangeType="end"
                                    onChange={changeEndDate}
                                    onFocus={closeDrawer}
                                />
                            )}
                        </ValidatedControl>
                    </Group>
                    <Group gap="l">
                        <div style={{width: 300}}>
                            <Label><RequiredMark label="Include"/></Label>
                            <DropDownTree
                                data={REPORT_OPTIONS_TREE}
                                checkedIdsList={reportOptions}
                                inputProps={{placeholder: 'Select report options', onFocus: closeDrawer}}
                                onTreeChange={params => setReportOptions(params.checkedIdsList)}
                            />
                        </div>
                        <div style={{width: 300}}>
                            <Label>User States</Label>
                            <UserStateSuggest
                                disabled={selectedUsers.length > 0}
                                userStates={userStates}
                                onSelect={selectUserStates}
                                onFocus={closeDrawer}
                            />
                        </div>
                    </Group>
                    <Group direction="v" gap="xs">
                        <Label>Select</Label>
                        <RadioButtons
                            name={SELECT_NAME}
                            value={selectMode}
                            onChange={changeSelectionMode}>
                            <Group gap="l">
                                <RadioButton {...SELECT_OPTIONS.USERS}/>
                                <RadioButton {...SELECT_OPTIONS.GROUPS}/>
                            </Group>
                        </RadioButtons>
                        {selectMode === SELECT_OPTIONS.USERS.value && (
                            <div style={{width: 624}}>
                                <UsersSuggest
                                    id="excel-plugin-usage-report-users-suggest"
                                    accountId={accountId}
                                    selectedUsers={selectedUsers}
                                    setSelectedUsers={changeSelectedUsers}
                                    placeholder="All Users"
                                    onFocus={closeDrawer}
                                />
                            </div>
                        )}
                        {selectMode === SELECT_OPTIONS.GROUPS.value && (
                            <GroupSuggest
                                id="usage-report-group-suggest"
                                width={300}
                                accountId={accountId}
                                selectedGroupId={selectedGroup}
                                placeholder="All Groups"
                                onSelect={group => setSelectedGroup(group?.id || null)}
                                onFocus={closeDrawer}
                            />
                        )}
                    </Group>
                </Group>
            </FormSection>
            <Dialog shown={!!criteria} setShown={closeBigReportNotification}>
                {criteria ? (
                    <BigReportWarningModal
                        reportType={ReportType.EXCEL_REPORT}
                        userUsageCriteria={criteria}
                        onSuccess={onBigReportRequested}
                        onClose={closeBigReportNotification}
                    />
                ) : null}
            </Dialog>
            <ExcelPluginUsageReportDrawer
                accountId={accountId}
                report={selectedReport}
                open={!!selectedReport}
                onClose={closeDrawer}
                onAsyncRun={openBigReportNotification}
                onSyncRun={downloadReportSync}
            />
        </ProfileMain>
    );
};
